import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';

const AdminUsers = () => {
  const { settings } = useSettings();
  const exchangeRate = Number(settings?.exchange_rate) || 40;
  const euroRate = Number(settings?.euro_rate) || exchangeRate;
  const binanceRate = Number(settings?.binance_rate) || exchangeRate;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('todos');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'cliente',
    is_wholesale: false,
    rate_type: 'bcv',
    custom_rate: null
  });

  const [creditData, setCreditData] = useState({
    creditLimitUSD: 0,
    creditLimitBS: 0,
    creditDays: 7,
    notes: ''
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/auth/users');
      setUsers(data.data || []);
    } catch (error) {
      console.error('Error:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone,
        password: '',
        role: user.role,
        is_wholesale: user.is_wholesale === 1,
        rate_type: user.rate_type || 'bcv',
        custom_rate: user.custom_rate || null
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '', email: '', phone: '', password: '',
        role: 'cliente', is_wholesale: false,
        rate_type: 'bcv', custom_rate: null
      });
    }
    setShowModal(true);
  };

  const handleOpenCreditModal = (user) => {
    setSelectedUser(user);
    const limitUSD = Number(user.credit_limit_usd) || 0;
    setCreditData({
      creditLimitUSD: limitUSD,
      creditLimitBS: limitUSD * exchangeRate,
      creditDays: user.credit_days || 7,
      notes: user.credit_notes || ''
    });
    setShowCreditModal(true);
  };

  const handleCreditLimitUSDChange = (value) => {
    const usd = parseFloat(value) || 0;
    setCreditData(prev => ({
      ...prev,
      creditLimitUSD: usd,
      creditLimitBS: usd * exchangeRate
    }));
  };

  const handleCreditLimitBSChange = (value) => {
    const bs = parseFloat(value) || 0;
    setCreditData(prev => ({
      ...prev,
      creditLimitBS: bs,
      creditLimitUSD: exchangeRate > 0 ? bs / exchangeRate : 0
    }));
  };

  const handleOpenHistory = async (user) => {
    setSelectedUser(user);
    try {
      const { data } = await api.get(`/credit/history/${user.id}`);
      setCreditHistory(data.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error:', error);
      setCreditHistory([]);
      setShowHistoryModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        is_wholesale: formData.is_wholesale,
        rate_type: formData.rate_type,
        custom_rate: formData.custom_rate
      };

      if (!editingUser) {
        payload.password = formData.password;
      }

      if (editingUser) {
        await api.put(`/auth/users/${editingUser.id}`, payload);
      } else {
        await api.post('/auth/users', payload);
      }
      setShowModal(false);
      fetchUsers();
      alert('✅ Guardado exitosamente');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/credit/enable/${selectedUser.id}`, creditData);
      setShowCreditModal(false);
      fetchUsers();
      alert('✅ Crédito actualizado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDisableCredit = async (userId) => {
    if (!window.confirm('¿Desactivar el crédito de este cliente?')) return;
    try {
      await api.post(`/credit/disable/${userId}`);
      fetchUsers();
      alert('✅ Crédito desactivado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAdjustScore = async (userId, points) => {
    const reason = prompt(`Motivo del ajuste de ${points > 0 ? '+' : ''}${points} puntos:`);
    if (!reason) return;
    try {
      await api.post(`/credit/adjust-score/${userId}`, { points, reason });
      fetchUsers();
      alert('✅ Puntos ajustados');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este usuario?')) return;
    try {
      await api.delete(`/auth/users/${id}`);
      fetchUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const getScoreLevel = (score) => {
    if (score >= 900) return { color: 'bg-green-100 text-green-700', icon: '🏆', label: 'Excelente' };
    if (score >= 700) return { color: 'bg-blue-100 text-blue-700', icon: '⭐', label: 'Muy bueno' };
    if (score >= 500) return { color: 'bg-yellow-100 text-yellow-700', icon: '✅', label: 'Bueno' };
    if (score >= 300) return { color: 'bg-orange-100 text-orange-700', icon: '⚠️', label: 'Regular' };
    return { color: 'bg-red-100 text-red-700', icon: '❌', label: 'Malo' };
  };

  const getRateInfo = (user) => {
    const type = user.rate_type || 'bcv';
    switch (type) {
      case 'binance':
        return { icon: '🪙', label: 'Binance', rate: binanceRate, color: 'bg-yellow-100 text-yellow-700' };
      case 'euro':
        return { icon: '💶', label: 'Euro BCV', rate: euroRate, color: 'bg-blue-100 text-blue-700' };
      case 'custom':
        return { icon: '⚙️', label: 'Custom', rate: user.custom_rate || 0, color: 'bg-purple-100 text-purple-700' };
      default:
        return { icon: '💵', label: 'BCV', rate: exchangeRate, color: 'bg-green-100 text-green-700' };
    }
  };

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700',
    motorizado: 'bg-orange-100 text-orange-700',
    cliente: 'bg-blue-100 text-blue-700',
    cliente_mayorista: 'bg-green-100 text-green-700'
  };

  const roleIcons = {
    admin: '👑',
    motorizado: '🚗',
    cliente: '🛒',
    cliente_mayorista: '🏪'
  };

  const roleLabels = {
    admin: 'Admin',
    motorizado: 'Motorizado',
    cliente: 'Cliente',
    cliente_mayorista: 'Mayorista'
  };

  const filtered = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
                        u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'todos' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const counts = {
    todos: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    motorizado: users.filter(u => u.role === 'motorizado').length,
    cliente: users.filter(u => u.role === 'cliente').length,
    cliente_mayorista: users.filter(u => u.role === 'cliente_mayorista').length
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold">👥 Gestionar Usuarios</h1>
          <p className="text-gray-500 text-sm mt-1">{users.length} usuarios registrados</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-gold text-white px-5 py-2.5 rounded-full font-semibold hover:bg-gold-dark transition shadow-md flex items-center gap-2 text-sm"
        >
          <span>+</span> Nuevo
        </button>
      </div>

      {/* Tasas actuales */}
      <div className="bg-gradient-to-r from-green-50 via-blue-50 to-yellow-50 rounded-xl p-4 mb-6 border border-gray-100">
        <div className="flex flex-wrap gap-4 justify-center md:justify-start">
          <div className="flex items-center gap-2">
            <span className="text-lg">💵</span>
            <div>
              <div className="text-xs text-gray-500">BCV Oficial</div>
              <div className="font-bold text-green-700">{exchangeRate} Bs</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">💶</span>
            <div>
              <div className="text-xs text-gray-500">Euro BCV</div>
              <div className="font-bold text-blue-700">{euroRate} Bs</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🪙</span>
            <div>
              <div className="text-xs text-gray-500">Binance</div>
              <div className="font-bold text-yellow-700">{binanceRate} Bs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="🔍 Buscar usuario..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold"
          />
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'todos', label: 'Todos', icon: '👥' },
              { value: 'admin', label: 'Admins', icon: '👑' },
              { value: 'motorizado', label: 'Motorizados', icon: '🚗' },
              { value: 'cliente', label: 'Clientes', icon: '🛒' },
              { value: 'cliente_mayorista', label: 'Mayoristas', icon: '🏪' }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilterRole(f.value)}
                className={`px-3 py-2 rounded-full text-xs font-medium transition flex items-center gap-1 ${
                  filterRole === f.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span>{f.icon}</span>
                <span className="hidden sm:inline">{f.label}</span>
                <span className="text-xs opacity-70">({counts[f.value]})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          <div className="text-6xl mb-4">👥</div>
          <p className="text-lg font-medium">No hay usuarios</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(user => {
            const scoreLevel = getScoreLevel(user.credit_score || 500);
            const isWholesale = user.is_wholesale === 1 || user.role === 'cliente_mayorista';
            const hasCredit = user.credit_enabled === 1;
            const rateInfo = getRateInfo(user);

            return (
              <div
                key={user.id}
                className={`rounded-xl shadow-sm border p-4 hover:shadow-md transition ${
                  isWholesale ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${
                      isWholesale
                        ? 'bg-gradient-to-br from-green-500 to-green-600'
                        : 'bg-gradient-to-br from-gold to-gold-dark'
                    }`}>
                      {user.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{user.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleColors[user.role]}`}>
                          {roleIcons[user.role]} {roleLabels[user.role]}
                        </span>
                        {hasCredit && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${scoreLevel.color}`}>
                            {scoreLevel.icon} {user.credit_score}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${rateInfo.color}`}>
                          {rateInfo.icon} {rateInfo.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-1 text-sm mb-3">
                  <div className="flex items-center gap-2 text-gray-600 text-xs">
                    <span>📧</span> <span className="truncate">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 text-xs">
                    <span>📱</span> <span>{user.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 text-xs">
                    <span>💱</span> <span>Tasa: {rateInfo.rate} Bs</span>
                  </div>
                </div>

                {hasCredit && (
                  <div className="bg-white rounded-lg p-3 mb-3 border border-gray-100">
                    <div className="text-xs text-gray-500 mb-2">💰 Línea de crédito</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Límite:</span>
                        <span className="font-semibold text-green-600">
                          ${Number(user.credit_limit_usd || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Usado:</span>
                        <span className="font-semibold text-red-500">
                          ${Number(user.credit_used_usd || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Disponible:</span>
                        <span className="font-semibold text-blue-600">
                          ${(Number(user.credit_limit_usd || 0) - Number(user.credit_used_usd || 0)).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plazo:</span>
                        <span className="font-semibold">{user.credit_days} días</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleOpenModal(user)}
                    className="flex-1 text-blue-500 hover:bg-blue-50 py-1.5 rounded-lg text-xs transition"
                  >
                    ✏️ Editar
                  </button>
                  {(user.role === 'cliente' || user.role === 'cliente_mayorista') && (
                    <button
                      onClick={() => handleOpenCreditModal(user)}
                      className="flex-1 text-green-500 hover:bg-green-50 py-1.5 rounded-lg text-xs transition"
                    >
                      💰 {hasCredit ? 'Crédito' : 'Activar'}
                    </button>
                  )}
                  {hasCredit && (
                    <>
                      <button
                        onClick={() => handleAdjustScore(user.id, 10)}
                        className="text-green-500 hover:bg-green-50 py-1.5 px-2 rounded-lg text-xs transition"
                        title="+10 puntos"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => handleAdjustScore(user.id, -10)}
                        className="text-red-500 hover:bg-red-50 py-1.5 px-2 rounded-lg text-xs transition"
                        title="-10 puntos"
                      >
                        👎
                      </button>
                      <button
                        onClick={() => handleOpenHistory(user)}
                        className="text-purple-500 hover:bg-purple-50 py-1.5 px-2 rounded-lg text-xs transition"
                        title="Ver historial"
                      >
                        📊
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-500 hover:bg-red-50 py-1.5 px-2 rounded-lg text-xs transition"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== MODAL CREAR/EDITAR USUARIO ===== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-serif font-bold">
                {editingUser ? '✏️ Editar' : '➕ Nuevo'} Usuario
              </h2>
              <button onClick={() => setShowModal(false)} className="text-2xl hover:text-gold">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Teléfono *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  required
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Contraseña *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={e => {
                    const role = e.target.value;
                    setFormData({
                      ...formData,
                      role,
                      is_wholesale: role === 'cliente_mayorista'
                    });
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                >
                  <option value="cliente">🛒 Cliente</option>
                  <option value="cliente_mayorista">🏪 Cliente Mayorista</option>
                  <option value="motorizado">🚗 Motorizado</option>
                  <option value="admin">👑 Administrador</option>
                </select>
              </div>

              {/* ===== SELECTOR DE TASA DE CAMBIO ===== */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-blue-800 mb-3">
                  💱 Tasa de cambio para este cliente
                </label>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { value: 'bcv', label: '💵 BCV Oficial', desc: `${exchangeRate} Bs` },
                    { value: 'binance', label: '🪙 Binance', desc: `${binanceRate} Bs` },
                    { value: 'euro', label: '💶 Euro BCV', desc: `${euroRate} Bs` },
                    { value: 'custom', label: '⚙️ Personalizada', desc: 'Manual' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, rate_type: option.value })}
                      className={`p-3 rounded-xl border-2 text-left transition ${
                        formData.rate_type === option.value
                          ? 'border-blue-500 bg-white shadow-md'
                          : 'border-blue-200 bg-white/50 hover:bg-white'
                      }`}
                    >
                      <div className="font-semibold text-sm">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                    </button>
                  ))}
                </div>

                {formData.rate_type === 'custom' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-blue-800 mb-1">
                      Tasa personalizada (Bs por USD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.custom_rate || ''}
                      onChange={e => setFormData({ ...formData, custom_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ej: 900.00"
                    />
                  </div>
                )}

                <p className="text-xs text-blue-600 mt-2">
                  ℹ️ Esta tasa se aplicará a todos los pedidos de este cliente
                </p>
              </div>

              {formData.role === 'cliente_mayorista' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
                  🏪 Este cliente verá precios al mayor y podrá hacer pedidos al por mayor
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 border rounded-full hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-gold text-white rounded-full hover:bg-gold-dark transition font-semibold"
                >
                  {editingUser ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== MODAL CRÉDITO ===== */}
      {showCreditModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreditModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold">
                💰 {selectedUser.credit_enabled ? 'Editar' : 'Activar'} Crédito
              </h2>
              <button onClick={() => setShowCreditModal(false)} className="text-2xl hover:text-gold">✕</button>
            </div>

            <div className="p-5">
              <div className="bg-blue-50 rounded-xl p-3 mb-4">
                <div className="text-sm font-medium">{selectedUser.name}</div>
                <div className="text-xs text-gray-600">
                  Puntos actuales: {selectedUser.credit_score || 500} {getScoreLevel(selectedUser.credit_score || 500).icon}
                </div>
                <div className="text-xs text-blue-700 mt-1">
                  💱 Tasa actual del cliente: 1 USD = {getRateInfo(selectedUser).rate} Bs ({getRateInfo(selectedUser).label})
                </div>
              </div>

              <form onSubmit={handleCreditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Límite de crédito (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={creditData.creditLimitUSD}
                    onChange={e => handleCreditLimitUSDChange(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Límite de crédito (Bs) 
                    <span className="text-xs text-gray-500 ml-2">
                      (calculado con la tasa del cliente)
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={creditData.creditLimitBS}
                    onChange={e => handleCreditLimitBSChange(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plazo de pago (días) *</label>
                  <select
                    value={creditData.creditDays}
                    onChange={e => setCreditData({...creditData, creditDays: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  >
                    <option value={3}>3 días (prueba)</option>
                    <option value={7}>1 semana (7 días)</option>
                    <option value={14}>2 semanas (14 días)</option>
                    <option value={21}>3 semanas (21 días)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notas</label>
                  <textarea
                    value={creditData.notes}
                    onChange={e => setCreditData({...creditData, notes: e.target.value})}
                    rows="2"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="Observaciones sobre el cliente..."
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreditModal(false)}
                    className="flex-1 py-3 border rounded-full hover:bg-gray-50 transition"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition font-semibold"
                  >
                    {selectedUser.credit_enabled ? 'Guardar' : 'Activar'}
                  </button>
                </div>

                {selectedUser.credit_enabled && (
                  <button
                    type="button"
                    onClick={() => {
                      handleDisableCredit(selectedUser.id);
                      setShowCreditModal(false);
                    }}
                    className="w-full py-3 border-2 border-red-200 text-red-500 rounded-full hover:bg-red-50 transition text-sm"
                  >
                    ❌ Desactivar crédito
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL HISTORIAL ===== */}
      {showHistoryModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowHistoryModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-5 flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold">
                📊 Historial de Crédito - {selectedUser.name}
              </h2>
              <button onClick={() => setShowHistoryModal(false)} className="text-2xl hover:text-gold">✕</button>
            </div>
            <div className="p-5">
              {creditHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-3">📭</div>
                  <p>Sin historial de crédito</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {creditHistory.map((h, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm capitalize">
                          {h.action.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(h.created_at).toLocaleString('es-VE')}
                        </div>
                        {h.notes && (
                          <div className="text-xs text-gray-600 italic mt-1">"{h.notes}"</div>
                        )}
                      </div>
                      <div className={`font-bold text-sm ${
                        h.points_change > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {h.points_change > 0 ? '+' : ''}{h.points_change} pts
                        <div className="text-xs text-gray-400 font-normal">
                          {h.points_before} → {h.points_after}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
